import * as React from "react";
import { gql, useQuery } from "@apollo/client";
import {
  FindByEntityTypeQueryQuery,
  NewCommunityEntitySubscriptionSubscription
} from "../../../../schemaTypes";
import { newCommunityEntitySubscription } from "../FindAll";

export const findByEntityTypeQuery = gql`
  query FindByEntityTypeQuery($entityType: String!) {
    findByEntityType(
      entityType: $entityType
    ) {
      id
      name
      shortName
      entityType
    }
  }
`;

export function useFindByEntityType(entityType: string) {
  const normalizedEntityType = entityType.trim().toLowerCase();
  const { data, loading, subscribeToMore } = useQuery<
    FindByEntityTypeQueryQuery
  >(findByEntityTypeQuery, {
    variables: { entityType },
    skip: !normalizedEntityType,
  });

  React.useEffect(() => {
    if (!subscribeToMore || !normalizedEntityType) return;

    const unsubscribe = subscribeToMore({
      document: newCommunityEntitySubscription,
      updateQuery: (prev, { subscriptionData }: {
        subscriptionData: { data?: NewCommunityEntitySubscriptionSubscription }
      }) => {
        if (!subscriptionData.data) return prev;

        const newCommunityEntity = subscriptionData.data.newCommunityEntity;
        const matchesEntityType = Array.isArray(newCommunityEntity.entityType)
          && newCommunityEntity.entityType.some(
            (value) => value.trim().toLowerCase() === normalizedEntityType
          );

        if (!matchesEntityType) {
          return prev;
        }

        const newCommunityEntityPwa: FindByEntityTypeQueryQuery["findByEntityType"][number] = {
          __typename: "CommunityEntityPWA",
          id: newCommunityEntity.id,
          name: newCommunityEntity.name,
          shortName: newCommunityEntity.shortName,
          entityType: newCommunityEntity.entityType,
        };

        const alreadyExists = prev.findByEntityType.some(
          (communityEntity) => communityEntity.id === newCommunityEntityPwa.id
        );

        if (alreadyExists) {
          return prev;
        }

        return {
          ...prev,
          findByEntityType: [...prev.findByEntityType, newCommunityEntityPwa]
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
        };
      }
    });

    return () => unsubscribe();
  }, [normalizedEntityType, subscribeToMore]);

  return {
    communityEntities: data?.findByEntityType ?? [],
    loading
  };
}
